// Derives --color-primary-hover and --color-primary-contrast from a single
// admin-picked color, so the admin only ever has to choose one color
// instead of three. Used identically before login (BrandingContext, from
// the public branding payload) and after login (from the tenant object
// already returned by /me, /auth/login, /auth/refresh) -- one function,
// applied via the same code path both times.

function hexToRgb(hex) {
  const value = hex.replace('#', '');
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const toHex = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Darkens a hex color by the given fraction (0-1) -- used for the hover state. */
export function darken(hex, amount = 0.12) {
  const { r, g, b } = hexToRgb(hex);
  const factor = 1 - amount;
  return rgbToHex({ r: r * factor, g: g * factor, b: b * factor });
}

/**
 * Simple perceptual-brightness heuristic (the "YIQ" formula) to pick
 * readable text on top of an arbitrary background color -- good enough for
 * a UI accent color, not a full WCAG/APCA contrast computation.
 */
export function contrastTextColor(hex) {
  const { r, g, b } = hexToRgb(hex);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150 ? '#000000' : '#ffffff';
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** Applies (or clears) a tenant's primary brand color as CSS custom properties on the document root. */
export function applyBrandColor(primaryColor) {
  const root = document.documentElement.style;
  if (!primaryColor || !HEX_COLOR_RE.test(primaryColor)) {
    root.removeProperty('--color-primary');
    root.removeProperty('--color-primary-hover');
    root.removeProperty('--color-primary-contrast');
    return;
  }

  root.setProperty('--color-primary', primaryColor);
  root.setProperty('--color-primary-hover', darken(primaryColor));
  root.setProperty('--color-primary-contrast', contrastTextColor(primaryColor));
}
